import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ShieldCheck, ArrowRight } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { ReportsData } from '../../../types/aai';
import { useAsync } from '../useAsync';
import { Loading, ErrorNote, AggregateView } from '../components';
import { Card, MonoLabel, StatCard, StageBand, BandPill, PageHeader } from '../dashboards/ui';

const ProgramReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useAsync<ReportsData>(() => aaiApi.getReports(), 'Could not load reports.');
  if (loading) return <Loading label="Loading reports…" />;
  if (error || !data) return <ErrorNote message={error || 'Something went wrong.'} />;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Reports"
        subtitle="Board-ready readouts and exports. Everything here is aggregate — safe to share upward."
      />

      {/* Report actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className="bg-white border border-gray-100 rounded-xl p-5">
          <span className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 grid place-items-center mb-3"><FileText size={20} /></span>
          <div className="font-bold text-[14px] mb-1">Board readout</div>
          <p className="text-[12.5px] text-gray-500 leading-relaxed mb-3">Adoption by department, company readiness, and registry status — the aggregate picture below.</p>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white text-[12.5px] font-semibold px-4 py-2 rounded-full transition-colors whitespace-nowrap">
            Print / save as PDF
          </button>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-5">
          <span className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 grid place-items-center mb-3"><ShieldCheck size={20} /></span>
          <div className="font-bold text-[14px] mb-1">Examiner export (registry)</div>
          <p className="text-[12.5px] text-gray-500 leading-relaxed mb-3">The full model inventory with risk classes and human-in-loop controls.</p>
          <button onClick={() => navigate('/aai/registry')} className="inline-flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[12.5px] font-semibold px-4 py-2 rounded-full transition-colors whitespace-nowrap">
            Open registry <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard value={data.enrolled} label="Enrolled" />
        <StatCard value={data.onboarded} label="Onboarded" />
        <StatCard value={<StageBand stage={data.company_stage} />} label="Company stage" />
        <StatCard value={data.use_cases} label="Approved use cases" />
      </div>

      <div className="mb-4"><AggregateView title="Company readiness" agg={data.company} /></div>

      <Card>
        <MonoLabel className="mb-3">By department</MonoLabel>
        {data.departments.map((d) => (
          <div key={d.department} className="flex items-center justify-between gap-3 py-2.5 border-t border-brand-100 first:border-0">
            <span className="font-semibold text-[13px] flex-1 min-w-0 truncate">{d.department}</span>
            <span className="text-[12px] text-gray-500 whitespace-nowrap">{d.onboarded} onboarded</span>
            <BandPill band={d.band} />
          </div>
        ))}
        {data.departments.length === 0 && <p className="text-[13px] text-gray-400">No department data yet.</p>}
      </Card>
    </div>
  );
};

export default ProgramReportsPage;
